import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook for animating numbers with intersection observer
 * @param {number} endValue - The final value to count to
 * @param {number} duration - Animation duration in milliseconds (default: 2000)
 * @param {number} delay - Delay before starting animation (default: 0)
 * @returns {[number, React.RefObject]} - Current counter value and ref to attach to element
 */
const useCounterAnimation = (endValue, duration = 2000, delay = 0) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            // Element is visible, start the counter
            setTimeout(() => {
              const startTime = Date.now();
              const startValue = 0;
              const steps = 60; // 60 frames
              const stepDuration = duration / steps;

              const timer = setInterval(() => {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(elapsed / duration, 1);

                // Easing function for smoother animation (easeOutCubic)
                const easeProgress = 1 - Math.pow(1 - progress, 3);
                
                const currentValue = Math.floor(startValue + (endValue - startValue) * easeProgress);
                setCount(currentValue);

                if (progress >= 1) {
                  clearInterval(timer);
                  setCount(endValue);
                  setHasAnimated(true);
                }
              }, stepDuration);

              return () => clearInterval(timer);
            }, delay);
          }
        });
      },
      {
        threshold: 0.3, // Trigger when 30% of element is visible
      }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      if (elementRef.current) {
        observer.unobserve(elementRef.current);
      }
    };
  }, [endValue, duration, delay, hasAnimated]);

  return [count, elementRef];
};

export default useCounterAnimation;
